import React, { useEffect } from 'react'
import type { PublicCmsItem, PublicCmsItemStatic } from 'apollo-gql'
import { useTranslation } from 'lib/i18n'
import logApolloError from 'lib/logApolloError'
import { trackCmsPageViewed } from 'lib/tracking/avo/trackPageView'

import BusinessPageFooter from 'components/BusinessPageFooter/BusinessPageFooter'
import CmsItem from 'components/CmsItem/CmsItem'
import CmsPageHeaderContent from 'components/CmsPageHeaderContent/CmsPageHeaderContent'
import CmsPageHeaderVideo from 'components/CmsPageHeaderVideo/CmsPageHeaderVideo'
import ConditionalWrapper from 'components/ConditionalWrapper/ConditionalWrapper'
import NotFound from 'components/NotFound/NotFound'
import NotFoundPage from 'components/NotFound/NotFoundPage'
import PublicLayout from 'components/PublicLayout/PublicLayout'
import Spinner from 'components/Spinner/Spinner'
import usePage from 'hooks/usePage'
import usePath from 'hooks/usePath'
import useResponsive from 'hooks/useResponsive'

import Seo from './Seo'

const cmsFooters: { [componentName: string]: React.FC } = {
  BusinessPageFooter,
}

interface CmsPageProps {
  slug: string
  useQueryProps?: {
    [key: string]: string
  }
  wrapItems?: boolean
  includeLayoutWithHeader?: boolean
}

const CmsPage: React.FC<CmsPageProps> = ({
  slug,
  useQueryProps = {},
  wrapItems = false,
  includeLayoutWithHeader = false,
}) => {
  const { t } = useTranslation()
  const { page, error, loading } = usePage(slug, useQueryProps)
  const { isMobile } = useResponsive()
  const path = usePath()

  useEffect(() => {
    if (!loading && !error && page) trackCmsPageViewed({ pageSlug: slug, path })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, slug])

  const notFoundProps = {
    title: t('cmsPage.slug.404.title', 'Page not found'),
    subTitle: t(
      'cmsPage.slug.404.subTitle',
      'Sorry, the page you were looking for does not exist.',
    ),
    backButtonText: t('cmsPage.slug.404.goBack', 'Try searching'),
    backButtonHref: '/search',
  }

  if (error) {
    logApolloError(
      `[ContentLoad Error] Error loading content for page: ${slug}`,
      error,
    )
  }

  if (!loading && !error && !page)
    return includeLayoutWithHeader ? (
      <NotFoundPage {...notFoundProps} />
    ) : (
      <NotFound {...notFoundProps} />
    )

  const cmsItems = page?.publicCmsItems || []
  const pageHeader = page?.cmsItemPageHeader
  const pageMetadata = page?.cmsItemPageMetadata

  const headerImage = isMobile
    ? pageHeader?.imageBackgroundMobile?.url
    : pageHeader?.imageBackgroundDesktop?.url

  const footerData = page?.cmsItemPageFooter as PublicCmsItemStatic
  const CmsFooter = cmsFooters[footerData?.component]

  return (
    <>
      <Seo pageMetadata={pageMetadata} />
      <ConditionalWrapper
        condition={includeLayoutWithHeader}
        wrapper={(children: React.ReactNode) => (
          <PublicLayout
            waveColor={pageHeader?.waveWrapperColor}
            headerVideo={
              pageHeader && <CmsPageHeaderVideo pageHeader={pageHeader} />
            }
            businessHeader={pageHeader?.businessPage}
            headerImage={headerImage}
            headerContent={
              pageHeader && <CmsPageHeaderContent pageHeader={pageHeader} />
            }
            footer={CmsFooter && <CmsFooter />}
          >
            {children}
          </PublicLayout>
        )}
      >
        <>
          {loading && <Spinner height={30} centered />}
          {cmsItems.map((cmsItem: PublicCmsItem, index) => (
            <CmsItem
              key={cmsItem.id}
              item={cmsItem}
              wrapItems={wrapItems}
              indexPositionFromTop={index}
            />
          ))}
        </>
      </ConditionalWrapper>
    </>
  )
}

export default CmsPage
